import { newTheme, darkTheme } from '@customerio/pluma-components/css';
import { themeVars } from '@customerio/pluma-components/css/theme-tokens';
import type { PlumaTheme } from '@customerio/pluma-components/css';

// shorthand aliases for the charcoal palette vars
const c = themeVars.palette;
const c900 = c['grey_charcoal-900'];
const c800 = c['grey_charcoal-800'];
const c700 = c['grey_charcoal-700'];
const c600 = c['grey_charcoal-600'];
const c500 = c['grey_charcoal-500'];
const c400 = c['grey_charcoal-400'];
const c300 = c['grey_charcoal-300'];
const c200 = c['grey_charcoal-200'];
const c100 = c['grey_charcoal-100'];
const c025 = c['grey_charcoal-025'];
const cWhite = c['grey_charcoal-white'];
const cBlack = c['grey_charcoal-black'];

/**
 * Hydra's custom dark mode theme.
 *
 * Token values reference the Pluma charcoal palette via themeVars
 * so they stay in sync if the palette is updated upstream and
 * changes are caught at compile time:
 *
 *   c900  #151515  — sidebar, top bar
 *   c800  #212121  — main content, card surfaces
 *   c700  #333333  — borders, hover/selected states
 *   c600  #474747  — disabled, very subtle chrome
 *   c500  #666666  — placeholder text
 *   c400  #8A8A8A  — minimal text
 *   c300  #ABABAB  — subtle text, section headers
 *   c200  #CCCCCC  — nav icons, secondary icons
 *   c100  #E0E0E0  — secondary text
 *   c025  #F5F5F5  — primary text
 *   white #FFFFFF  — bold/active text
 *
 * A few values between palette steps (#2A2A2A, #262626, #1A1A1A)
 * don't have palette tokens and use hex directly.
 *
 * Non-color tokens (spacing, radius, typography, motion) come from
 * the "new" theme. Because Hydra uses `withGlobalVariables`, the
 * theme tokens are applied as CSS custom properties on :root via a
 * <style> tag — the VE className is not used at runtime.
 */
export const hydraDarkTheme: PlumaTheme = {
  ...darkTheme,
  name: 'dark',
  colorScheme: 'dark',

  tokens: {
    // Non-color tokens are shared between light and dark.
    ...newTheme.tokens,

    // Pluma dark as the semantic base, design-spec neutrals on top.
    color: {
      ...darkTheme.tokens.color,

      // ── Backgrounds ──────────────────────────────────────────────
      'background-default': c800,

      // ── Surfaces ─────────────────────────────────────────────────
      'surface-base': c800,
      'surface-base-hover': '#2A2A2A', // between 800 and 700
      'surface-base-disabled': c700,
      'surface-subtle': '#262626', // between 800 and 700
      'surface-subtle-hover': c700,
      'surface-subtle-disabled': '#1A1A1A', // between 900 and 800
      'surface-bold': c900,
      'surface-bold-hover': '#1A1A1A', // between 900 and 800
      'surface-active': c700,
      'surface-active-hover': c800,
      'surface-active-disabled': c900,

      // ── Borders ──────────────────────────────────────────────────
      'border-base': c700,
      'border-base-hover': c600,
      'border-minimal': c700,
      'border-minimal-hover': c600,
      'border-bold': c025,
      'border-bold-hover': c100,
      'border-active': c600,
      'border-active-hover': c500,
      'border-dropdown': c700,

      // ── Text ─────────────────────────────────────────────────────
      'text-base': c025,
      'text-base-hover': cWhite,
      'text-base-disabled': c600,
      'text-bold': cWhite,
      'text-bold-hover': c025,
      'text-active': cWhite,
      'text-subtle': c300,
      'text-placeholder': c500,
      'text-minimal': c400,
      'text-invert': cBlack,

      // ── Icons ────────────────────────────────────────────────────
      'icon-base': c025,
      'icon-base-hover': cWhite,
      'icon-base-disabled': c600,
      'icon-bold': cWhite,
      'icon-subtle': c200,
      'icon-minimal': c600,
      'icon-invert': cBlack,
      'icon-navigation': c200,
      'icon-button': c025,

      // ── Navigation ───────────────────────────────────────────────
      'navigation-background': c900,
      'navigation-background-hover': c800,
      'navigation-background-selected': c700,
      'navigation-text': c100,
      'navigation-text-selected': cWhite,
      'navigation-icon': c200,
      'navigation-icon-collapsed': c200,

      // ── Toggle ───────────────────────────────────────────────────
      'toggle-surface-base': c600,
      'toggle-surface-base-hover': c700,
      'toggle-surface-base-disabled': '#262626',
    },
  },

  tokensMeta: darkTheme.tokensMeta,
  styles: newTheme.styles,
};
